import Create from '@mui/icons-material/Create'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import Publish from '@mui/icons-material/Publish'
import {
	Alert,
	AlertTitle,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TableCell,
	Theme,
	Typography
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { percent } from 'csx'
import { action, runInAction } from 'mobx'
import { observer, useLocalStore } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import {
	BaseFormPackageInfoModel,
	FormPackageInfo,
} from '../../../../../api/DTOtemp'
import { FormPackageClient } from '../../../../../api/clients/identity/FormPackageClient'
import { AssignmentType } from '../../../../../api/clients/workflowTasks/DTOs'
import UserChip from '../../../../../components/chips/UserChip'
import { FullscreenSpinner } from '../../../../../components/feedback/circular'
import { TableColumn } from '../../../../../components/tables/DTO'
import { FilteredTable } from '../../../../../components/tables/FilteredTable'
import { FilterControl, SelectFilterOption } from '../../../../../components/tables/filters/DTO'
import { useModals } from '../../../../../services/notifications/ModalService'
import { usePrompts } from '../../../../../services/notifications/PromptService'
import { toastService } from '../../../../../services/notifications/ToastService'
import AdministrationPageContainer from '../../AdministrationPageContainer'
import { updateBreadcrumbs } from '../../Breadcrumbs'
import { PackageCreateDialog } from './FormPackageCreateDialog'
import { UploadFormPackageDialog } from './UploadFormPackageDialog'

export const FormPackagesPage = observer(() => {
	const styles = useStyles()
	const history = useHistory()
	const promptService = usePrompts()

	const localStore = useLocalStore(() => ({
		createPackageOpen: false,
		packageList: undefined as BaseFormPackageInfoModel[] | undefined
	}))

	updateBreadcrumbs('/_administration/_form-packages', 'Form Packages')

	const packageApi = new FormPackageClient()
	const modalService = useModals()

	useEffect(() => {
		const packagePromise = packageApi.GetFormPackages()
		packagePromise
			.then((v) => {
				runInAction(() => localStore.packageList = v.data)
			})
			.catch((v) => {
				console.error(v)
			})
	}, [])

	const handleCreateClicked = action(() =>
		localStore.createPackageOpen = true
	)

	const handleCreatePackageClosed = action(() =>
		localStore.createPackageOpen = false
	)

	const handleCreatePackage = (name: string, description: string) => {
		packageApi
			.CreateFormPackage({ name, description })
			.then((response) => {
				toastService.displayToast({
					message: 'Form package created',
					area: 'global',
					severity: 'success',
				})
				return response.data.id
			})
			.then((packageId) => {
				history.push(`/_administration/_form-packages/${packageId}`)
				handleCreatePackageClosed()
			})
			.catch(() => {
				toastService.displayToast({
					message: 'Error creating form package',
					area: 'global',
					severity: 'error',
				})
			})
	}

	const handlePackageDeleted = action((packageId: number) => {
		const packageDeletePromise = packageApi.DeleteFormPackage(packageId)

		const originalPackageList = localStore.packageList
		const newPackageList = (localStore.packageList ?? []).filter((v) => v.id !== packageId)

		console.log('new package list: ', newPackageList)

		localStore.packageList = newPackageList

		packageDeletePromise
			.then((v) => {
				toastService.displayToast({
					message: 'Form package deleted',
					area: 'global',
					severity: 'success',
				})
			})
			.catch((v) => {
				console.error(v)
				runInAction(() => localStore.packageList = originalPackageList)
				toastService.displayToast({
					message: 'Error deleting form package',
					area: 'global',
					severity: 'error',
				})
			})
	})

	const handleRouteToPackage = (packageId: number) => {
		history.push(`/_administration/_form-packages/${packageId}`)
	}

	const getUserOptions = useMemo(action((): SelectFilterOption[] => {
		const userOptions: Record<string, unknown> = {}
		localStore.packageList?.forEach((p) => userOptions[p.lastModifiedBy] = true)
		return Object.keys(userOptions).map((k) => ({
			label: <UserChip
				assignmentType={AssignmentType.User}
				id={Number(k)}
			/>,
			value: Number(k)
		}))
	}), [localStore.packageList])

	const filterControls: FilterControl[] = useMemo(() => [
		{
			type: 'date range',
			name: 'lastModifiedDate',
			label: 'Last Edited'
		},
		{
			type: 'multiselect',
			name: 'lastModifiedBy',
			label: "Edited By",
			options: getUserOptions
		},
		{
			type: 'search',
			name: 'search',
			column: ["name", "description"]
		}
	], [localStore.packageList])

	const columns: TableColumn<BaseFormPackageInfoModel>[] = useMemo(() => [
		{
			name: "name",
			label: "Package Name"
		},
		{
			name: "description",
			label: "Package Description"
		},
		{
			name: "lastModifiedDate",
			label: "Last Edited",
			renderContent: (v) => new Date(v.lastModifiedDate).toLocaleString()
		},
		{
			name: "lastModifiedBy",
			label: "Edited By",
			renderContent: (v) =>
				<UserChip
					assignmentType={AssignmentType.User}
					id={v.lastModifiedBy}
				/>
		},
		{
			renderHeading: <TableCell key={4} align="right">Actions</TableCell>,
			renderCell: (v: BaseFormPackageInfoModel) =>
				<TableCell key={4} align="right">
					<IconButton onClick={() => handleRouteToPackage(v.id)}>
						<Edit fontSize="small" />
					</IconButton>
					<IconButton
						onClick={() => {
							promptService
								.showDialog((props) => (
									<ConfirmDeleteFormPackagePrompt
										packageName={v.name}
										onCancel={() => props.close('cancel')}
										onConfirm={() => props.close('okay')}
									/>
								))
								.then((response) => {
									if (response === 'okay') handlePackageDeleted(v.id)
								})
						}}
					>
						<Delete fontSize="small" />
					</IconButton>
				</TableCell>
		}
	], [])

	return (
		<AdministrationPageContainer
			title="Form Packages"
			actions={
				<div>
					<Button
						className={styles.firstButton}
						variant="outlined"
						color="primary"
						startIcon={<Publish />}
						onClick={() =>
							modalService
								.showForm((props) => (
									<UploadFormPackageDialog
										existingPackageNames={localStore.packageList?.map((v) => v.name) ?? []}
										onCancel={() => props.close({ closeResult: 'cancel' })}
										onConfirm={(uploadedPackage) =>
											props.close({
												closeResult: 'okay',
												value: uploadedPackage,
											})
										}
									/>
								))
								.then((response) => {
									if (response.closeResult === 'okay')
										handleRouteToPackage((response.value as FormPackageInfo).id)
								})
						}
					>
						Upload Package
					</Button>
					<Button
						color="primary"
						variant="contained"
						onClick={handleCreateClicked}
						startIcon={<Create />}
					>
						Create Package
					</Button>
				</div>
			}
		>
			{localStore.packageList === undefined ? <FullscreenSpinner /> : (
				<FilteredTable
					columns={columns}
					allEntries={localStore.packageList}
					filterControls={filterControls}
				/>
			)}
			<PackageCreateDialog
				open={localStore.createPackageOpen}
				onClose={handleCreatePackageClosed}
				existingPackageNames={localStore.packageList?.map((v) => v.name) ?? []}
				onCreatePackage={handleCreatePackage}
			/>
		</AdministrationPageContainer>
	)
})

type ConfirmDeleteFormPackagePromptProps = {
	packageName: string
	onConfirm: () => void
	onCancel: () => void
}

const ConfirmDeleteFormPackagePrompt = (
	props: ConfirmDeleteFormPackagePromptProps,
) => {
	const styles = useStyles()

	return (
		<>
			<DialogTitle>Confirm Delete Form Package</DialogTitle>
			<DialogContent>
				<Typography>
					Are you sure that you want to delete <b>{props.packageName}</b>?
				</Typography>
				<Alert variant="outlined" severity="warning" className={styles.alert}>
					<AlertTitle>Warning</AlertTitle>
					This action will delete all forwarded and saved versions of this
					package.
				</Alert>
			</DialogContent>
			<DialogActions>
				<Button color="primary" onClick={props.onCancel}>
					Cancel
				</Button>
				<Button color="primary" variant="contained" onClick={props.onConfirm}>
					Confirm
				</Button>
			</DialogActions>
		</>
	)
}

const useStyles = makeStyles((theme: Theme) => ({
	tableRoot: {
		width: percent(100),
		height: 'fit-content',
		overflow: 'auto',
	},

	firstButton: {
		marginRight: theme.spacing(2),
	},

	alert: {
		margin: theme.spacing(2, 0),
	}
}))
